/* eslint-disable @typescript-eslint/no-shadow */
import React from 'react';
import { FileUploader } from '@citrite/citrix-files-ui';
import { Item as ShareFileItem } from '@citrite/sf-api';
import * as SparkMD5 from 'spark-md5';
import { useAppStore } from '../../../common/store';
import { SUPPORTED_FILE_EXTENSIONS } from '../../../constants';
import { DocgenClient } from '../../../data/docgenClient';
import { handleErrorWithServerMessage } from '../../../data/errorHandler';
import {
	CopyFileFromSharefilePayload,
	FileUploadCompletePayload,
	FileUploadPayload,
	FileUploadResponse,
} from '../../../data/interfaces/DocumentTemplateFile';
import { t } from '../../../utils';
import { FileChooser as SfFileChooser } from '../FileChooser';
import { ProgressState } from '../SelectDocument/types';
import { CustomFileUploderStyle } from './CustomFileUploader.styled';

interface CustomFileUploaderProps {
	setUploadState: (state: ProgressState) => void;
	resetUploadState: () => void;
}

export function CustomFileUploader({
	setUploadState,
	resetUploadState,
}: CustomFileUploaderProps) {
	const { fetchThumbnailUrl, documentTemplate, updateDocumentTemplate } = useAppStore(
		state => ({
			fetchThumbnailUrl: state.fetchThumbnailUrl,
			updateDocumentTemplate: state.updateDocumentTemplate,
			documentTemplate: state.documentTemplate,
		})
	);

	const [isSfFilePickerVisible, setIsSfFilePickerVisible] =
		React.useState<boolean>(false);

	const onSfFilePickerClose = React.useCallback((file: ShareFileItem) => {
		setIsSfFilePickerVisible(false);
		if (file !== null) {
			uploadFromSharefile(file);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const updateFileName = (fileName: string) => {
		updateDocumentTemplate({ file: { ...documentTemplate.file, fileName } });
	};

	const completeUploadAndFetchThumbnail = (fileName: string) => {
		try {
			fetchThumbnailUrl(documentTemplate?.rid);

			updateFileName(fileName);
			setUploadState('ready');
		} catch {
			handleErrorWithServerMessage(t('errorMessages.fetchThumbnailUrlError'));
			resetUploadState();
		}
	};
	const uploadFromSharefile = async (file: ShareFileItem) => {
		setUploadState('started');

		const { documentTemplate } = useAppStore.getState();
		const payload: CopyFileFromSharefilePayload = {
			fileName: file.FileName,
			fileId: file.Id,
			overwrite: true,
		};
		try {
			const response = await DocgenClient.copyFileFromSharefile(
				documentTemplate?.rid,
				payload
			);
			if (response !== null) {
				setUploadState('storing');
				completeUploadAndFetchThumbnail(file.FileName);
			}
		} catch {
			handleErrorWithServerMessage(t('errorMessages.uploadFileError'));
			resetUploadState();
		}
	};

	const uploadFromComputer = async (uploadedLocalFiles: [File]) => {
		const file = uploadedLocalFiles[0];

		const { documentTemplate } = useAppStore.getState();
		setUploadState('started');
		const payload: FileUploadPayload = {
			fileName: file.name,
			fileSize: file.size,
		};
		try {
			const response: FileUploadResponse = await DocgenClient.createFileUpload(
				payload,
				documentTemplate?.rid
			);
			const { chunkUri, method } = response;

			uploadFile(method, chunkUri, file);
		} catch {
			handleErrorWithServerMessage(t('errorMessages.uploadFileError'));
			resetUploadState();
		}
	};

	const uploadFile = async (method, chunkUri, file) => {
		try {
			const { documentTemplate } = useAppStore.getState();
			let uploadedFile;
			if (method === 'Standard') {
				const formData = new FormData();
				formData.append('Filedata', file);

				const fileUploadResponse = await fetch(chunkUri, {
					method: 'POST',
					body: formData,
				});
				const jsonResponse = await fileUploadResponse.json();
				const jsonFileResponse = jsonResponse?.value?.[0];
				uploadedFile = {
					id: jsonFileResponse.id,
					filename: jsonFileResponse.filename,
				};
			} else if (method === 'CloudNativeStandard') {
				const fileMd5Hash = await computeMd5Hash(file);

				const fileUploadResponse = await fetch(chunkUri, {
					method: 'PUT',
					body: file,
					headers: {
						'Content-MD5': `${fileMd5Hash}`,
					},
				});

				const jsonFileResponse = await fileUploadResponse.json();

				uploadedFile = {
					id: jsonFileResponse.id,
					filename: jsonFileResponse.fileName,
				};
			} else {
				throw new Error('Unsupported upload method: ' + method);
			}

			setUploadState('downloading');

			const uploadCompletePayload: FileUploadCompletePayload = {
				fileName: uploadedFile.filename,
				fileId: uploadedFile.id,
				overwrite: true,
			};
			await DocgenClient.fileUploadComplete(documentTemplate.rid, uploadCompletePayload);
			setUploadState('storing');
			completeUploadAndFetchThumbnail(uploadedFile.filename);
		} catch (error: any) {
			handleErrorWithServerMessage(error);
			resetUploadState();
		}
	};

	const computeMd5Hash = async (file: File) => {
		return new Promise((resolve, reject) => {
			const chunkSize = 2097152; // Read in chunks of 2MB
			const spark = new SparkMD5.ArrayBuffer();
			const fileReader = new FileReader();

			let cursor = 0; // current cursor in file

			fileReader.onerror = function (): void {
				reject('MD5 computation failed - error reading the file');
			};

			// read chunk starting at `cursor` into memory
			function processChunk(chunkStart: number): void {
				const chunkEnd = Math.min(file.size, chunkStart + chunkSize);
				fileReader.readAsArrayBuffer(file.slice(chunkStart, chunkEnd));
			}

			// when it's available in memory, process it
			// If using TS >= 3.6, you can use `FileReaderProgressEvent` type instead
			// of `any` for `e` variable, otherwise stick with `any`
			// See https://github.com/Microsoft/TypeScript/issues/25510
			fileReader.onload = function (e: any): void {
				spark.append(e.target.result); // Accumulate chunk to md5 computation
				cursor += chunkSize; // Move past this chunk

				if (cursor < file.size) {
					// Enqueue next chunk to be accumulated
					processChunk(cursor);
				} else {
					// Computation ended, last chunk has been processed. Return as Promise value.
					// This returns the base64 encoded md5 hash, which is what cloud services expect
					resolve(btoa(spark.end(true)));
				}
			};

			processChunk(0);
		});
	};

	return (
		<CustomFileUploderStyle data-testid="CustomFileUploader">
			<FileUploader
				files={[]}
				allowFolders={false}
				forceSingleFile
				browseShareFile={() => {
					setIsSfFilePickerVisible(true);
				}}
				fileOrMimeTypes={SUPPORTED_FILE_EXTENSIONS}
				allowFilesToBeAdded
				onAddItems={uploadFromComputer}
			/>

			{isSfFilePickerVisible && <SfFileChooser onClose={onSfFilePickerClose} />}
		</CustomFileUploderStyle>
	);
}
